import {
    ApiCountryEstimateSchema,
    type ApiCountryEstimate,
} from "@bridge-applications/pb-types";
import { z } from "zod";

const CACHE_KEY = "pbtg:country-analytics-context:v2";
const CacheSchema = z
    .object({
        schemaVersion: z.literal(2),
        userId: z.uuid(),
        telegramUserId: z.number().int().positive().nullable(),
        estimate: ApiCountryEstimateSchema,
    })
    .strict();
type Cache = z.infer<typeof CacheSchema>;
type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** A disposable projection for synchronous event capture, not application state. */
export function createCountryAnalyticsCache() {
    let cached: Cache | null = null;
    let storage: StorageLike | null = null;
    let confirmedUserId: string | null = null;
    return {
        begin(
            nextStorage: StorageLike | null,
            expectedTelegramId: number | undefined,
        ): void {
            storage = nextStorage;
            cached = null;
            confirmedUserId = null;
            if (!storage || expectedTelegramId === undefined) return;
            try {
                const raw = storage.getItem(CACHE_KEY);
                if (!raw) return;
                const parsed = CacheSchema.safeParse(JSON.parse(raw));
                if (
                    parsed.success &&
                    parsed.data.telegramUserId === expectedTelegramId
                )
                    cached = parsed.data;
                else storage.removeItem(CACHE_KEY);
            } catch {
                /* Persistence is optional. */
            }
        },
        confirm(userId: string, telegramUserId: number | null): void {
            confirmedUserId = userId;
            if (
                cached &&
                (cached.userId !== userId ||
                    cached.telegramUserId !== telegramUserId)
            ) {
                cached = null;
                try {
                    storage?.removeItem(CACHE_KEY);
                } catch {
                    /* Optional cache. */
                }
            }
        },
        clear(): void {
            cached = null;
            confirmedUserId = null;
            try {
                storage?.removeItem(CACHE_KEY);
            } catch {
                /* Optional cache. */
            }
        },
        store(
            userId: string,
            telegramUserId: number | null,
            estimate: ApiCountryEstimate,
        ): boolean {
            if (confirmedUserId !== userId) return false;
            const parsed = CacheSchema.safeParse({
                schemaVersion: 2,
                userId,
                telegramUserId,
                estimate,
            });
            if (!parsed.success) return false;
            if (
                cached &&
                cached.userId === userId &&
                Date.parse(cached.estimate.ec_valid_until) >
                    Date.parse(estimate.ec_valid_until)
            )
                return false;
            cached = parsed.data;
            try {
                storage?.setItem(CACHE_KEY, JSON.stringify(cached));
            } catch {
                /* Memory still works. */
            }
            return true;
        },
        properties(
            expectedTelegramId: number | undefined,
            now = Date.now(),
        ): Record<string, string | number | boolean | null> {
            if (
                !cached ||
                (expectedTelegramId !== undefined
                    ? cached.telegramUserId !== expectedTelegramId
                    : confirmedUserId !== cached.userId)
            ) {
                return {
                    ec_country: null,
                    ec_quality: 0,
                    ec_version: null,
                };
            }
            const expired = Date.parse(cached.estimate.ec_valid_until) <= now;
            return {
                ...countryEstimateProperties(cached.estimate),
                ...(expired
                    ? {
                          ec_country: null,
                          ec_quality: 0,
                      }
                    : {}),
            };
        },
    };
}

export function countryEstimateProperties(
    estimate: ApiCountryEstimate,
): Record<string, string | number | boolean | null> {
    return {
        ec_country: estimate.ec_country,
        ec_quality: estimate.ec_quality,
        ec_version: estimate.ec_version,
    };
}

// A browser document owns this cache. The Provider Kit slice owns reactive state.
export const countryAnalyticsCache = createCountryAnalyticsCache();

interface CountryPersonClient {
    get_distinct_id(): string;
    setPersonProperties(
        properties: Record<string, string | number | boolean | null>,
    ): unknown;
}

export function syncCountryPersonProperties(
    client: CountryPersonClient,
    storage: Pick<StorageLike, "getItem" | "setItem"> | null,
    userId: string,
    estimate: ApiCountryEstimate,
    now = Date.now(),
): boolean {
    try {
        if (
            client.get_distinct_id() !== userId ||
            Date.parse(estimate.ec_valid_until) <= now
        )
            return false;
    } catch {
        return false;
    }
    const properties = {
        ...countryEstimateProperties(estimate),
        ec_valid_until: estimate.ec_valid_until,
    };
    // A renewed validity window must update the Person even if its country is unchanged.
    const signature = JSON.stringify(properties);
    const key = `pbtg:posthog-country-profile:v2:${userId}`;
    try {
        if (storage?.getItem(key) === signature) return false;
    } catch {
        /* Optional deduplication. */
    }
    try {
        client.setPersonProperties(properties);
    } catch {
        return false;
    }
    try {
        storage?.setItem(key, signature);
    } catch {
        /* Optional deduplication. */
    }
    return true;
}
